import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { ImpersonationDialog } from '@/components/ImpersonationDialog';
import { GLASS_SURFACE } from '@/components/shared/glass';
import { cn } from '@/lib/utils';

/**
 * Global customer / lead search, styled as a glass field. It opens the
 * existing ImpersonationDialog; ⌘K / Ctrl+K opens it from anywhere.
 * `iconOnly` is the compact mobile variant.
 */
export default function SearchTrigger({ iconOnly = false }: { iconOnly?: boolean }) {
    const { t } = useTranslation('crm');
    const triggerRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (iconOnly) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                triggerRef.current?.click();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [iconOnly]);

    const label = t('chrome.searchCustomersAndLeads');

    return (
        <ImpersonationDialog>
            <button
                ref={triggerRef}
                type="button"
                aria-label={label}
                title={iconOnly ? label : undefined}
                className={cn(
                    GLASS_SURFACE,
                    'flex h-9 items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-background hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
                    iconOnly ? 'w-9 justify-center' : 'w-full max-w-[420px] gap-2 px-3'
                )}
            >
                <Search aria-hidden="true" className="size-4 shrink-0" />
                {!iconOnly && (
                    <>
                        <span className="min-w-0 flex-1 truncate text-start text-sm">{label}</span>
                        <kbd className="hidden select-none items-center rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium sm:inline-flex">
                            <span dir="ltr">⌘K</span>
                        </kbd>
                    </>
                )}
            </button>
        </ImpersonationDialog>
    );
}
